/* eslint-disable react/jsx-key */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CurrentRoundContext } from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/LeaderboardTableFetcher';
import { PredictorCurrentRoundContext } from 'widgets/brownlow-predictor-v2/js/components/BrownlowPredictor';
import { getTranslation } from 'common/react/utils/translations';
import StatsTableBodyStandardCell from './StatsTableBodyStandardCell';
import StatsTableBodyAwardCell from './StatsTableBodyAwardCell';

const TABLE_COLS = 7;

export const StatsTableBody = ({
    rows,
    getTableBodyProps,
    prepareRow,
    getBenchmark,
    primaryTooltipComponent,
    getCellId,
    getCellTitle,
    customCells,
    skipCells,
    valueWrapper,
    isProspect,
    showPredictedData,
    isBrownlowV2,
    isBrownlowTracker,
    getTrProps = () => null
}) => {
    const isProspectTable = isProspect ?? false;

    const PredictorCurrentRound = useContext(PredictorCurrentRoundContext);
    const LeaderboardCurrentRound = useContext(CurrentRoundContext);
    const currentRoundNumber = PredictorCurrentRound
        ? PredictorCurrentRound
        : LeaderboardCurrentRound;

    return (
        <tbody {...getTableBodyProps()}>
            {!rows.length ? (
                <tr className="stats-table__body-row">
                    <td className="stats-table__cell" colSpan={TABLE_COLS}>
                        {getTranslation('label.search.noResultsFound')}
                    </td>
                </tr>
            ) : (
                rows.map((row, rowIndex) => {
                    prepareRow(row);

                    const hasWildcard = Boolean(row?.original?.secondCutOff);

                    const firstCutOffRow =
                        rowIndex === row?.original?.firstCutOff - 1
                            ? 'is-qualifier-row'
                            : '';

                    const secondCutOffRow =
                        rowIndex === row?.original?.secondCutOff - 1
                            ? 'is-qualifier-row is-qualifier-row--wildcard'
                            : '';

                    const isWildcardHighlight =
                        hasWildcard &&
                        rowIndex >= row?.original?.firstCutOff &&
                        rowIndex <= row?.original?.secondCutOff - 1
                            ? 'is-wildcard-highlight'
                            : '';

                    const isConferenceRow =
                        row?.original?.isConference && rowIndex === 1
                            ? 'is-conference-row'
                            : '';

                    const clubRowClass = row?.original?.clubName?.abbreviation
                        ? `stats-table__body-row--team-${row?.original?.clubName?.abbreviation?.toLowerCase()}`
                        : '';
                    const isFavourite = row?.original?.isFavourite
                        ? 'stats-table__body-row--favourite-team'
                        : '';

                    const isLiveRow = row?.values?.isPlaying ? 'is-live' : '';

                    const cells = row.cells.filter((cell) => {
                        if (!skipCells) {
                            return true;
                        }
                        return !skipCells.includes(cell.column.id);
                    });

                    return (
                        <tr
                            {...row.getRowProps()}
                            className={classnames(
                                'stats-table__body-row',
                                clubRowClass,
                                isWildcardHighlight,
                                isConferenceRow,
                                isLiveRow,
                                isFavourite,
                                firstCutOffRow,
                                secondCutOffRow
                            )}
                            {...getTrProps(row)}
                            key={rowIndex}
                        >
                            {cells.map((cell, cellIndex) => {
                                const cellProps = cell.getCellProps();

                                // Custom cell override
                                const renderCustomCell =
                                    customCells?.[cell.column.id];
                                if (renderCustomCell) {
                                    return renderCustomCell(
                                        row,
                                        cell,
                                        cellProps,
                                        rowIndex
                                    );
                                }

                                // If Prospect Table, exit
                                /* eslint-disable no-undefined */
                                if (
                                    cell.value === undefined &&
                                    isProspectTable
                                ) {
                                    return null;
                                }

                                // Common variables
                                const benchmark = getBenchmark
                                    ? getBenchmark(row, cell)
                                    : null;
                                const isUpcoming =
                                    Number.parseInt(cell.column.Header) >
                                    currentRoundNumber;
                                const value = cell.value ?? 0;

                                // Render award-specific or standard cell
                                if (isBrownlowV2 || isBrownlowTracker) {
                                    return (
                                        <StatsTableBodyAwardCell
                                            key={cellIndex}
                                            cellIndex={cellIndex}
                                            cellProps={cellProps}
                                            valueWrapper={valueWrapper}
                                            value={value}
                                            row={row}
                                            cell={cell}
                                            isUpcoming={isUpcoming}
                                            showPredictedData={
                                                showPredictedData
                                            }
                                            isBrownlowTracker={
                                                isBrownlowTracker
                                            }
                                            primaryTooltipComponent={
                                                primaryTooltipComponent
                                            }
                                            getCellId={getCellId}
                                            getCellTitle={getCellTitle}
                                            benchmark={benchmark}
                                        />
                                    );
                                }

                                return (
                                    <StatsTableBodyStandardCell
                                        key={cellIndex}
                                        cellIndex={cellIndex}
                                        cellProps={cellProps}
                                        cell={cell}
                                        row={row}
                                        value={value}
                                        benchmark={benchmark}
                                        isUpcoming={isUpcoming}
                                        valueWrapper={valueWrapper}
                                        primaryTooltipComponent={
                                            primaryTooltipComponent
                                        }
                                        getCellId={getCellId}
                                        getCellTitle={getCellTitle}
                                    />
                                );
                            })}
                        </tr>
                    );
                })
            )}
        </tbody>
    );
};

StatsTableBody.propTypes = {
    rows: PropTypes.array.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    prepareRow: PropTypes.func.isRequired,
    getBenchmark: PropTypes.func,
    primaryTooltipComponent: PropTypes.func,
    getCellId: PropTypes.func,
    getCellTitle: PropTypes.func,
    customCells: PropTypes.object,
    skipCells: PropTypes.array,
    valueWrapper: PropTypes.bool,
    isProspect: PropTypes.bool,
    getTrProps: PropTypes.func,
    showPredictedData: PropTypes.bool,
    isBrownlowV2: PropTypes.bool,
    isBrownlowTracker: PropTypes.bool
};
